/**
 * Risk Report Store
 *
 * Manages risk assessment reports for both anonymous and authenticated users.
 * - Anonymous: in-memory session reports (lost on page refresh)
 * - Authenticated: persisted to Firestore (max 50 reports)
 * - On login: session reports migrate to Firestore automatically
 * - Auto-saves on every analysis, FIFO cleanup at MAX_REPORTS.
 */
import { create } from 'zustand';
import portfolioService from '../services/portfolioService';
import { auth } from '../firebase';

import { logger } from '../services/loggingService';
import { getErrorMessage } from '../utils/validation';
import analyticsService from '../services/analytics';
import type { SampleAnalysisResult, SampleAnalysisHolding } from './portfolioAnalysisStore';

/* The caller's ID token, read fresh per call — the savedPortfolioStore shape.
 * ([[ROADMAP 1.24]] step 2) Every read and write here goes through the
 * gateway now, so a staging browser reads STAGING's reports — including the
 * scheduled ones the backend was already writing there, which this rail
 * could never see while it read production directly ([[BUGS #144]], and the
 * invites 404 of [[BUGS #243]]). The FIFO cap and the 60s duplicate window
 * moved server-side with the writes, where they bind instead of advise. */
const idToken = async (): Promise<string | null> => {
  try {
    const user = auth.currentUser;
    return user ? await user.getIdToken() : null;
  } catch {
    return null;
  }
};

export interface RiskReportSummary {
  id: string;
  portfolioName: string;
  holdingsCount: number;
  portfolioValue: number;
  riskScore: number;
  riskLevel: string;
  createdAt: Date;
}

const MAX_SESSION_REPORTS = 10;

interface RiskReportState {
  reports: RiskReportSummary[];              // Firestore-backed (auth'd users)
  sessionReports: RiskReportSummary[];       // In-memory only (anonymous users)
  activeReport: SampleAnalysisResult | null;
  activeReportId: string | null;
  isLoading: boolean;
  isLoadingReport: boolean;
  error: string | null;
  abortController: AbortController | null;

  loadReports: (userId: string) => Promise<void>;
  saveReport: (userId: string, result: SampleAnalysisResult) => Promise<string | null>;
  saveSessionReport: (result: SampleAnalysisResult) => string;
  loadSessionReport: (reportId: string) => void;
  loadReport: (userId: string, reportId: string) => Promise<void>;
  deleteReport: (userId: string, reportId: string) => Promise<void>;
  /** True when the name was kept. A report could be deleted but never renamed
   *  ([[BUGS #509]]), so a run kept whatever name was guessed for it. */
  renameReport: (reportId: string, name: string) => Promise<boolean>;
  deleteReports: (userId: string, reportIds: string[]) => Promise<void>;
  migrateSessionToFirestore: (userId: string) => Promise<void>;
  clearSession: () => void;
  clearActiveReport: () => void;
  cleanup: () => void;
}

// MAX_REPORTS and the 60s duplicate window moved SERVER-side with the writes
// ([[ROADMAP 1.24]] step 2 — `_risk_reports.py`), where they bind rather than
// advise; the collection name went with them.

/**
 * Generate a descriptive report name from analysis data.
 *
 * Report History is an AUDIT trail of analysis runs — distinct from the
 * Saved Portfolio list. Multiple reports for the same portfolio (same
 * name, different dates) are expected and useful: a user analyzing
 * "My Tech Portfolio" today, again next week, sees two rows in the
 * side panel with the same name + different timestamps.
 *
 * Naming priority (2026-06-14 fix):
 *   1. User's chosen portfolioName if set to a non-default value
 *      (i.e. anything other than 'My Portfolio' / empty / undefined).
 *      This is what the user typed in the analyze form.
 *   2. Otherwise fall back to a structural descriptor built from the
 *      holdings shape ("Tech-Heavy Growth (7 stocks)", "AAPL-Concentrated
 *      (3 stocks)"). Useful for sample portfolios and quick-analysis
 *      flows where no name was typed.
 *
 * The date stamp lives separately in `createdAt` and is rendered next
 * to the name in the side panel — no need to embed it here.
 */
export function generateReportName(result: SampleAnalysisResult): string {
  // Priority 1: user-typed name from the analyze form.
  const userName = (result.portfolioName || '').trim();
  if (userName && userName !== 'My Portfolio') {
    return userName;
  }

  // Priority 2: structural descriptor.
  const holdings = result.holdings || [];
  const count = holdings.length;
  const riskLevel = result.risk_level || '';

  const getHoldingValue = (h: SampleAnalysisHolding): number => h.market_value || h.value || 0;

  // Check concentration: does top holding exceed 30%?
  const totalValue = result.portfolio_value || holdings.reduce((s, h) => s + getHoldingValue(h), 0) || 1;
  const sorted = [...holdings].sort((a, b) => getHoldingValue(b) - getHoldingValue(a));
  const topHolding = sorted[0];
  const topPct = topHolding ? (getHoldingValue(topHolding) / totalValue) * 100 : 0;

  // Check sector dominance
  const sectorWeights: Record<string, number> = {};
  // A holding with no sector says nothing about the book's tilt — counted as
  // "Unknown" it named whole books "Unknown-Heavy" ([[BUGS #509]]).
  holdings.forEach((h) => {
    const sector = (h.sector || '').trim();
    if (!sector || sector === 'Unknown' || sector === 'Other') return;
    sectorWeights[sector] = (sectorWeights[sector] || 0) + getHoldingValue(h);
  });
  const topSectorEntry = Object.entries(sectorWeights).sort((a, b) => b[1] - a[1])[0];
  const topSectorPct = topSectorEntry ? (topSectorEntry[1] / totalValue) * 100 : 0;

  // Build descriptor
  let descriptor: string;
  if (count <= 3 && topHolding) {
    descriptor = `${topHolding.symbol}-Focused`;
  } else if (topPct > 30 && topHolding) {
    descriptor = `${topHolding.symbol}-Concentrated`;
  } else if (topSectorPct > 50 && topSectorEntry) {
    const sectorShort = topSectorEntry[0].replace(/ Services| Products/g, '');
    descriptor = `${sectorShort}-Heavy`;
  } else if (count >= 20) {
    descriptor = 'Diversified';
  } else {
    // Use risk level as descriptor
    descriptor = riskLevel === 'High' ? 'Growth'
      : riskLevel === 'Low' ? 'Conservative'
      : 'Balanced';
  }

  return `${descriptor} (${count} stock${count !== 1 ? 's' : ''})`;
}

// Module-level cache for full report data (avoids re-fetching on sidebar click)
const reportDataCache = new Map<string, SampleAnalysisResult>();

// Prevents concurrent session migration
let _isMigrating = false;

export const useRiskReportStore = create<RiskReportState>((set, get) => ({
  reports: [],
  sessionReports: [],
  activeReport: null,
  activeReportId: null,
  isLoading: false,
  isLoadingReport: false,
  error: null,
  abortController: null,

  loadReports: async (userId: string) => {
    const { abortController: oldController } = get();
    oldController?.abort();

    const abortController = new AbortController();
    set({ abortController, isLoading: true, error: null });

    try {
      const token = await idToken();
      if (!token || !userId) {
        set({ isLoading: false });
        return;
      }
      const res = await portfolioService.listRiskReports(token);
      if (abortController.signal.aborted) return;

      // Summaries only — the blob is fetched per click via `loadReport`, the
      // same rule the rebalance list states: a rail of fifty must not
      // decompress fifty reports. The instant-click cache still fills as
      // reports are opened or saved this session.
      const reports: RiskReportSummary[] = (res.reports || []).map((r) => ({
        id: r.id,
        portfolioName: r.portfolioName || 'Untitled',
        holdingsCount: r.holdingsCount || 0,
        portfolioValue: r.portfolioValue || 0,
        riskScore: r.riskScore || 0,
        riskLevel: r.riskLevel || 'Unknown',
        // Defensive: an unparseable date sorts to the epoch and groups under
        // "Earlier" rather than crashing the layout — same rule as always.
        createdAt: (() => {
          const d = new Date(r.createdAt);
          return isNaN(d.getTime()) ? new Date(0) : d;
        })(),
      }));

      // Merge with existing optimistic entries — DO NOT replace. The race the
      // merge defeats survives the migration intact: a save resolves at t=X,
      // the optimistic update fires at t=X+1ms, and a list request started at
      // t=0 can return at t=X+50ms without the just-written row. A naive
      // `set({ reports })` would erase the optimistic entry and show "No
      // reports yet" right after a successful save (seen live 2026-06-14).
      set((state) => {
        const snapshotIds = new Set(reports.map((r) => r.id));
        const optimisticOnly = state.reports.filter((r) => !snapshotIds.has(r.id));
        const merged = [...optimisticOnly, ...reports].sort(
          (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
        );
        return { reports: merged, isLoading: false };
      });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      logger.error('Failed to load risk reports', { error: getErrorMessage(err) });
      set({ error: 'Failed to load risk reports', isLoading: false });
    }
  },

  saveReport: async (userId: string, result: SampleAnalysisResult) => {
    // The dedup window and the FIFO cap live SERVER-side now ([[ROADMAP 1.24]]
    // step 2) — in the browser they were a courtesy any stale tab could skip.
    // The server answers with `removedIds` (a Strict-Mode double-fire it
    // replaced, an oldest row the cap evicted), so the rail drops those rows
    // without a refetch.
    const reportName = generateReportName(result);
    set({ isLoading: true, error: null });
    try {
      const token = await idToken();
      if (!token || !userId) {
        set({ isLoading: false });
        return null;
      }
      const res = await portfolioService.saveRiskReport({
        portfolioName: reportName,
        holdingsCount: result.holdings?.length || 0,
        portfolioValue: result.portfolio_value || 0,
        riskScore: result.risk_score || 0,
        riskLevel: result.risk_level || 'Unknown',
        // Serialized, NOT the raw object ([[BUGS #134]]): the analysis result
        // carries nested arrays Firestore rejects, and the blob is an opaque
        // re-display artefact — the summary fields above exist precisely so
        // this one never needs shape rules.
        reportData: JSON.stringify(result),
      }, token);

      if (!res.success || !res.id) {
        set({ error: 'Failed to save risk report', isLoading: false });
        return null;
      }

      const removed = new Set(res.removedIds || []);
      removed.forEach((id) => reportDataCache.delete(id));

      // Optimistic prepend instead of a refetch — avoids the re-render
      // cascade that used to trigger duplicate auto-saves.
      const newReport: RiskReportSummary = {
        id: res.id,
        portfolioName: reportName,
        holdingsCount: result.holdings?.length || 0,
        portfolioValue: result.portfolio_value || 0,
        riskScore: result.risk_score || 0,
        riskLevel: result.risk_level || 'Unknown',
        createdAt: new Date(),
      };
      reportDataCache.set(res.id, result);
      set((state) => ({
        reports: [newReport,
          ...state.reports.filter((r) => r.id !== res.id && !removed.has(r.id))],
        isLoading: false,
      }));

      logger.logCrud('create', 'risk_report', res.id, true);
      analyticsService.trackRiskReportSaved(
        res.id,
        result.holdings?.length || 0,
        result.risk_score || 0
      );
      return res.id;
    } catch (err) {
      // Surface the real error — the generic message hid silent failures for
      // hours pre-2026-06-14, and a 4xx from the gateway carries its reason.
      const message = getErrorMessage(err);
      logger.logCrud('create', 'risk_report', '', false, message);
      set({ error: `Failed to save risk report: ${message}`, isLoading: false });
      return null;
    }
  },

  loadReport: async (userId: string, reportId: string) => {
    // Check cache first
    const cached = reportDataCache.get(reportId);
    if (cached) {
      set({ activeReport: cached, activeReportId: reportId, isLoadingReport: false });
      analyticsService.trackRiskReportLoaded(reportId);
      return;
    }

    set({ isLoadingReport: true, error: null });
    try {
      const token = await idToken();
      if (!token) {
        set({ error: 'Report not found', isLoadingReport: false });
        return;
      }
      // `get_report` has always read the runtime project (own + shared), which
      // is exactly why it needed no migration — the store simply joins it.
      const res = await portfolioService.getReport(reportId, token);
      const raw = (res as { reportData?: unknown }).reportData;
      if (!res.success || raw == null) {
        set({ error: 'Report not found', isLoadingReport: false });
        return;
      }
      // String = the serialized form every persisted report has (#134); the
      // object branch only guards a malformed row.
      const reportData = (typeof raw === 'string'
        ? JSON.parse(raw)
        : raw) as SampleAnalysisResult;

      reportDataCache.set(reportId, reportData);
      set({ activeReport: reportData, activeReportId: reportId, isLoadingReport: false });
      analyticsService.trackRiskReportLoaded(reportId);
    } catch (err) {
      logger.error('Failed to load risk report', { error: getErrorMessage(err) });
      set({ error: 'Failed to load risk report', isLoadingReport: false });
    }
  },

  renameReport: async (reportId: string, name: string) => {
    const clean = name.replace(/\s+/g, ' ').trim();
    if (!clean) return false;
    try {
      const token = await idToken();
      if (!token) return false;
      const res = await portfolioService.renameRiskReport(reportId, clean, token);
      if (!res.success) throw new Error(res.error || 'rename refused');
      const kept = res.portfolioName || clean;
      const cached = reportDataCache.get(reportId);
      if (cached) reportDataCache.set(reportId, { ...cached, portfolioName: kept });
      set((state) => ({
        reports: state.reports.map((r) => (r.id === reportId ? { ...r, portfolioName: kept } : r)),
        activeReport: state.activeReportId === reportId && state.activeReport
          ? { ...state.activeReport, portfolioName: kept } : state.activeReport,
      }));
      logger.logCrud('update', 'risk_report', reportId, true);
      return true;
    } catch (err) {
      logger.logCrud('update', 'risk_report', reportId, false, getErrorMessage(err));
      set({ error: 'Could not rename that report. Try again.' });
      return false;
    }
  },

  deleteReport: async (userId: string, reportId: string) => {
    set({ isLoading: true, error: null });
    try {
      const token = await idToken();
      if (!token) { set({ isLoading: false }); return; }
      const res = await portfolioService.deleteRiskReports([reportId], token);
      if (!res.success) throw new Error('delete refused');

      reportDataCache.delete(reportId);
      set((state) => ({
        reports: state.reports.filter((r) => r.id !== reportId),
        activeReport: state.activeReportId === reportId ? null : state.activeReport,
        activeReportId: state.activeReportId === reportId ? null : state.activeReportId,
        isLoading: false,
      }));

      logger.logCrud('delete', 'risk_report', reportId, true);
      analyticsService.trackRiskReportDeleted(reportId);
    } catch (err) {
      logger.logCrud('delete', 'risk_report', reportId, false, getErrorMessage(err));
      set({ error: 'Failed to delete risk report', isLoading: false });
    }
  },

  deleteReports: async (userId: string, reportIds: string[]) => {
    if (reportIds.length === 0) return;
    set({ isLoading: true, error: null });
    try {
      const token = await idToken();
      if (!token) { set({ isLoading: false }); return; }
      // One request for the whole multi-select — the server reports partial
      // failure per id rather than all-or-nothing.
      const res = await portfolioService.deleteRiskReports(reportIds, token);
      const failed = new Set(res.failedIds || []);
      if (failed.size > 0) {
        logger.error('Some risk reports failed to delete',
          { total: reportIds.length, failed: failed.size });
      }
      const removed = reportIds.filter((id) => !failed.has(id));
      const idsSet = new Set(removed);
      removed.forEach((id) => reportDataCache.delete(id));

      set((state) => ({
        reports: state.reports.filter((r) => !idsSet.has(r.id)),
        activeReport: state.activeReportId && idsSet.has(state.activeReportId) ? null : state.activeReport,
        activeReportId: state.activeReportId && idsSet.has(state.activeReportId) ? null : state.activeReportId,
        isLoading: false,
      }));

      logger.info('Bulk deleted risk reports', { count: removed.length });
      removed.forEach((id) => analyticsService.trackRiskReportDeleted(id));
    } catch (err) {
      logger.error('Failed to bulk delete risk reports', { error: getErrorMessage(err) });
      set({ error: 'Failed to delete reports', isLoading: false });
    }
  },

  // Save a report for anonymous users (in-memory + cache only)
  saveSessionReport: (result: SampleAnalysisResult) => {
    const reportName = generateReportName(result);
    const reportId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const summary: RiskReportSummary = {
      id: reportId,
      portfolioName: reportName,
      holdingsCount: result.holdings?.length || 0,
      portfolioValue: result.portfolio_value || 0,
      riskScore: result.risk_score || 0,
      riskLevel: result.risk_level || 'Unknown',
      createdAt: new Date(),
    };

    // Cache full data for instant loading on sidebar click
    reportDataCache.set(reportId, result);

    set((state) => ({
      sessionReports: [summary, ...state.sessionReports].slice(0, MAX_SESSION_REPORTS),
    }));

    return reportId;
  },

  // Load a session report from cache
  loadSessionReport: (reportId: string) => {
    const cached = reportDataCache.get(reportId);
    if (cached) {
      set({ activeReport: cached, activeReportId: reportId, isLoadingReport: false });
    }
  },

  // Migrate session reports to Firestore on login
  migrateSessionToFirestore: async (userId: string) => {
    const { sessionReports, saveReport } = get();
    if (_isMigrating || !userId || sessionReports.length === 0) return;

    _isMigrating = true;
    try {
      // Migrate oldest-first so newest ends up on top
      const reportsToMigrate = [...sessionReports].reverse();
      for (const report of reportsToMigrate) {
        const cachedData = reportDataCache.get(report.id);
        if (cachedData) {
          await saveReport(userId, cachedData);
          reportDataCache.delete(report.id); // Clean up session cache entry
        }
      }

      set({ sessionReports: [] });
    } finally {
      _isMigrating = false;
    }
  },

  // Clear session reports (e.g., on logout)
  clearSession: () => {
    const { sessionReports } = get();
    // Clean up cached data for session reports
    sessionReports.forEach(r => reportDataCache.delete(r.id));
    set({ sessionReports: [] });
  },

  clearActiveReport: () => {
    set({ activeReport: null, activeReportId: null });
  },

  cleanup: () => {
    const { abortController } = get();
    abortController?.abort();
    set({ abortController: null });
  },
}));
